/**
 * WP-50 — the in-house budget grid (plan 8.1; R21: no licensed grid
 * component, no spreadsheet library; react-window is the only thing under it).
 *
 * COMPOSITION. Three react-window VariableSizeGrid instances and one static
 * corner, so the frozen header rows and label columns scroll in sync with the
 * body (plan 8.1 "frozen header rows and label columns"):
 *
 *   corner (static)      header band  -> scrolls with the body in X
 *   left band (Y sync)   body         -> owns the scrollbars
 *
 * The header band carries a column-letter ruler as its first row and the
 * sheet's frozen rows under it; the left band carries a row-number ruler as
 * its first column and the sheet's frozen label columns beside it. One grid
 * per band means one scrollTo call per axis, and no measurement — which is
 * also why the grid takes explicit width/height props (jsdom has no layout).
 *
 * Design: `.claude/skills/siteoperations-design/SKILL.md` v2 — primary accent
 * only, dark classes on every surface, dense laptop-first controls.
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { VariableSizeGrid } from 'react-window';
import type { GridChildComponentProps, GridOnItemsRenderedProps, GridOnScrollProps } from 'react-window';
import { columnIndexToLetter, formatCellRef } from './engineBridge';
import { describeCell, editTextFor } from './display';
import { cellAt, findSheet, keyOf, WorkingSheet } from './model';
import { useBudgetGrid } from './useBudgetGrid';
import {
  BudgetGridHandle,
  BudgetGridProps,
  CellFocusEvent,
  CellRange,
  GridCell,
  WsChange,
} from './types';

const DEFAULT_WIDTH = 960;
const DEFAULT_HEIGHT = 420;
const DEFAULT_ROW_HEIGHT = 26;
const DEFAULT_COL_WIDTH = 112;
const DEFAULT_LABEL_WIDTH = 200;
const RULER_WIDTH = 44;
const RULER_HEIGHT = 22;
const DEFAULT_SPARE_ROWS = 2;
const DEFAULT_SPARE_COLS = 1;

type Band = 'corner' | 'header' | 'label' | 'body';

interface Selection {
  sheet: string;
  range: CellRange;
  /** The anchor: where an edit, a paste or a range extension starts. */
  active: { row: number; col: number };
  /** The moving end of a shift-extended range. */
  lead: { row: number; col: number };
}

const cellBase =
  'relative flex items-center overflow-hidden whitespace-nowrap border-b border-r border-gray-200 px-2 dark:border-gray-700';

function normalise(a: { row: number; col: number }, b: { row: number; col: number }): CellRange {
  return {
    r1: Math.min(a.row, b.row),
    c1: Math.min(a.col, b.col),
    r2: Math.max(a.row, b.row),
    c2: Math.max(a.col, b.col),
  };
}

function changeKey(sheet: string, ref: string): string {
  return String(sheet).trim().toUpperCase() + '!' + String(ref).toUpperCase();
}

/**
 * What every band's cell renderer is handed. react-window creates its cells
 * with `createElement(children, ...)`, so `children` MUST be a stable
 * component type — an inline arrow would be a new type on every render and
 * React would unmount and remount every cell, taking the focus out of the
 * open editor with it. Hence: three module-level components, and everything
 * that changes travels through `itemData`.
 */
interface BandData {
  renderCell: (row: number, col: number, style: React.CSSProperties, band: Band) => React.ReactElement | null;
  frozenRows: number;
  frozenCols: number;
}

const rulerCellClass =
  ' justify-center bg-gray-50 text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:bg-gray-800/50 dark:text-gray-500';

function HeaderBandCell({ columnIndex, rowIndex, style, data }: GridChildComponentProps<BandData>) {
  const col = data.frozenCols + 1 + columnIndex;
  if (rowIndex === 0) {
    return (
      <div style={style} data-testid={'gc-colhead-' + columnIndexToLetter(col)} className={cellBase + rulerCellClass}>
        {columnIndexToLetter(col)}
      </div>
    );
  }
  return data.renderCell(rowIndex, col, style, 'header');
}

function LeftBandCell({ columnIndex, rowIndex, style, data }: GridChildComponentProps<BandData>) {
  const row = data.frozenRows + 1 + rowIndex;
  if (columnIndex === 0) {
    return (
      <div style={style} data-testid={'gc-rowhead-' + String(row)} className={cellBase + rulerCellClass}>
        {String(row)}
      </div>
    );
  }
  return data.renderCell(row, columnIndex, style, 'label');
}

function BodyCell({ columnIndex, rowIndex, style, data }: GridChildComponentProps<BandData>) {
  return data.renderCell(data.frozenRows + 1 + rowIndex, data.frozenCols + 1 + columnIndex, style, 'body');
}

/** Proposed rows win the paint over accepted ones; rejected rows paint nothing. */
function indexChanges(changes: WsChange[] | undefined): Map<string, WsChange> {
  const map = new Map<string, WsChange>();
  if (!changes) return map;
  changes.forEach((change) => {
    if (!change || change.status === 'rejected') return;
    const key = changeKey(change.sheet, change.cell_ref);
    const existing = map.get(key);
    if (!existing || change.status === 'proposed' || existing.status !== 'proposed') {
      map.set(key, change);
    }
  });
  return map;
}

export const BudgetGrid = forwardRef<BudgetGridHandle, BudgetGridProps>(function BudgetGrid(props, ref) {
  const {
    sheets,
    changes,
    activeSheet,
    defaultActiveSheet,
    onActiveSheetChange,
    initialSelection,
    readOnly = false,
    width = DEFAULT_WIDTH,
    height = DEFAULT_HEIGHT,
    rowHeight = DEFAULT_ROW_HEIGHT,
    columnWidth = DEFAULT_COL_WIDTH,
    labelColumnWidth = DEFAULT_LABEL_WIDTH,
    spareRows = DEFAULT_SPARE_ROWS,
    spareCols = DEFAULT_SPARE_COLS,
    showLabels = true,
    onChange,
    onInsertColumn,
    onInsertRow,
    onSelectionChange,
    onCellFocus,
    onCommentHover,
    className,
  } = props;

  const model = useBudgetGrid(sheets, readOnly, {
    onChange: onChange,
    onInsertColumn: onInsertColumn,
    onInsertRow: onInsertRow,
  });

  const bodyRef = useRef<VariableSizeGrid>(null);
  const headerRef = useRef<VariableSizeGrid>(null);
  const leftRef = useRef<VariableSizeGrid>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const visibleRef = useRef({ rowStart: 0, rowStop: 0, colStart: 0, colStop: 0 });
  const skipBlurRef = useRef(false);

  const visibleSheets = model.sheets.filter((s) => !s.hidden);
  const [uncontrolledSheet, setUncontrolledSheet] = useState<string | null>(defaultActiveSheet || null);
  const fallbackName = visibleSheets.length > 0 ? visibleSheets[0].name : (model.sheets[0] ? model.sheets[0].name : '');
  const wantedName = activeSheet || uncontrolledSheet || fallbackName;
  const sheet: WorkingSheet | null = findSheet(model.sheets, wantedName) || (visibleSheets[0] || model.sheets[0] || null);
  const sheetName = sheet ? sheet.name : '';

  const frozenRows = sheet ? sheet.frozenRows : 0;
  const frozenCols = sheet ? sheet.frozenCols : 0;

  const defaultSelection = useCallback((): Selection => {
    const row = frozenRows + 1;
    const col = frozenCols + 1;
    const seeded = initialSelection;
    if (seeded) {
      return {
        sheet: sheetName,
        range: seeded,
        active: { row: seeded.r1, col: seeded.c1 },
        lead: { row: seeded.r2, col: seeded.c2 },
      };
    }
    return {
      sheet: sheetName,
      range: { r1: row, c1: col, r2: row, c2: col },
      active: { row: row, col: col },
      lead: { row: row, col: col },
    };
  }, [sheetName, frozenRows, frozenCols, initialSelection]);

  const [selectionState, setSelectionState] = useState<Selection>(defaultSelection);
  const selection = selectionState.sheet === sheetName ? selectionState : defaultSelection();

  const [editing, setEditing] = useState<{ sheet: string; row: number; col: number; text: string } | null>(null);
  const [menu, setMenu] = useState<{ x: number; y: number; row: number; col: number } | null>(null);

  const changeIndex = useMemo(() => indexChanges(changes), [changes]);

  const widthOf = useCallback(
    (col: number): number => {
      if (sheet && sheet.columnWidths[col]) return sheet.columnWidths[col];
      return col <= frozenCols ? labelColumnWidth : columnWidth;
    },
    [sheet, frozenCols, labelColumnWidth, columnWidth],
  );

  let frozenWidth = 0;
  for (let c = 1; c <= frozenCols; c += 1) frozenWidth += widthOf(c);
  const cornerWidth = RULER_WIDTH + frozenWidth;
  const headerHeight = RULER_HEIGHT + frozenRows * rowHeight;
  const bodyWidth = Math.max(120, width - cornerWidth);
  const bodyHeight = Math.max(rowHeight, height - headerHeight);

  const totalRows = sheet ? sheet.maxRow + spareRows : 0;
  const totalCols = sheet ? sheet.maxCol + spareCols : 0;
  const bodyRowCount = Math.max(0, totalRows - frozenRows);
  const bodyColCount = Math.max(0, totalCols - frozenCols);

  // react-window caches item metadata by index: after an insertion the widths
  // and the item count moved, so the caches are dropped from index 0.
  useEffect(() => {
    const reset = { columnIndex: 0, rowIndex: 0, shouldForceUpdate: true };
    if (bodyRef.current) bodyRef.current.resetAfterIndices(reset);
    if (headerRef.current) headerRef.current.resetAfterIndices(reset);
    if (leftRef.current) leftRef.current.resetAfterIndices(reset);
  }, [model.rev, sheetName, frozenCols, frozenRows, columnWidth, labelColumnWidth, rowHeight]);

  const focusEventFor = useCallback(
    (row: number, col: number): CellFocusEvent | null => {
      if (!sheet) return null;
      const ref2 = formatCellRef(row, col);
      const cell: GridCell | null = cellAt(sheet, row, col);
      const change = changeIndex.get(changeKey(sheetName, ref2)) || null;
      const described = describeCell(model.ctx, sheetName, ref2, cell, sheet.edited.has(keyOf(row, col)), change);
      return {
        sheet: sheetName,
        cell_ref: ref2,
        row: row,
        col: col,
        cell: cell,
        display: described.text,
        value: described.value,
        formula: described.formula,
        label: described.label,
        provenance: described.provenance,
        comment: cell && cell.comment_text
          ? { text: String(cell.comment_text), author: cell.comment_author ? String(cell.comment_author) : null }
          : null,
        change: change,
      };
    },
    [sheet, sheetName, changeIndex, model.ctx],
  );

  const ensureVisible = useCallback(
    (row: number, col: number) => {
      if (!bodyRef.current) return;
      const bodyRow = row - frozenRows - 1;
      const bodyCol = col - frozenCols - 1;
      if (bodyRow < 0 && bodyCol < 0) return;
      const v = visibleRef.current;
      const rowOutside = bodyRow >= 0 && (bodyRow < v.rowStart || bodyRow > v.rowStop);
      const colOutside = bodyCol >= 0 && (bodyCol < v.colStart || bodyCol > v.colStop);
      if (!rowOutside && !colOutside) return;
      bodyRef.current.scrollToItem({
        rowIndex: Math.max(0, bodyRow),
        columnIndex: Math.max(0, bodyCol),
      });
    },
    [frozenRows, frozenCols],
  );

  const applySelection = useCallback(
    (
      range: CellRange,
      active: { row: number; col: number },
      lead: { row: number; col: number },
      notifyFocus: boolean,
    ) => {
      setSelectionState({ sheet: sheetName, range: range, active: active, lead: lead });
      if (onSelectionChange) {
        onSelectionChange({
          sheet: sheetName,
          range: range,
          active: { row: active.row, col: active.col, ref: formatCellRef(active.row, active.col) },
        });
      }
      if (notifyFocus && onCellFocus) {
        const event = focusEventFor(active.row, active.col);
        if (event) onCellFocus(event);
      }
      ensureVisible(lead.row, lead.col);
    },
    [sheetName, onSelectionChange, onCellFocus, focusEventFor, ensureVisible],
  );

  const moveActive = useCallback(
    (dRow: number, dCol: number, extend: boolean) => {
      if (!sheet) return;
      const from = extend ? selection.lead : selection.active;
      const next = {
        row: Math.min(Math.max(1, from.row + dRow), totalRows),
        col: Math.min(Math.max(1, from.col + dCol), totalCols),
      };
      if (extend) {
        applySelection(normalise(selection.active, next), selection.active, next, true);
      } else {
        applySelection({ r1: next.row, c1: next.col, r2: next.row, c2: next.col }, next, next, true);
      }
    },
    [sheet, selection, totalRows, totalCols, applySelection],
  );

  const startEditing = useCallback(
    (row: number, col: number, seed: string | null) => {
      if (readOnly || !sheet) return;
      const cell = cellAt(sheet, row, col);
      // Each editing session starts with a clean blur guard: a keyboard commit
      // sets it, and a browser that does not fire blur on unmount would
      // otherwise leave it set for the next editor.
      skipBlurRef.current = false;
      setEditing({
        sheet: sheetName,
        row: row,
        col: col,
        text: seed === null ? editTextFor(cell) : seed,
      });
    },
    [readOnly, sheet, sheetName],
  );

  const commitEditing = useCallback(
    (move: { row: number; col: number } | null) => {
      if (!editing) return;
      const target = editing;
      setEditing(null);
      model.commitEdit(target.sheet, target.row, target.col, target.text);
      if (move) {
        const next = {
          row: Math.min(Math.max(1, move.row), totalRows),
          col: Math.min(Math.max(1, move.col), totalCols),
        };
        applySelection({ r1: next.row, c1: next.col, r2: next.row, c2: next.col }, next, next, true);
      }
    },
    [editing, model, totalRows, totalCols, applySelection],
  );

  const clearRange = useCallback(() => {
    if (readOnly || !sheet) return;
    const block: string[][] = [];
    for (let r = selection.range.r1; r <= selection.range.r2; r += 1) {
      const line: string[] = [];
      for (let c = selection.range.c1; c <= selection.range.c2; c += 1) line.push('');
      block.push(line);
    }
    model.pasteBlock(sheetName, selection.range.r1, selection.range.c1, block);
  }, [readOnly, sheet, selection, model, sheetName]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (editing) return;
      const key = event.key;
      const meta = event.ctrlKey || event.metaKey;

      if (meta && (key === 'z' || key === 'Z')) {
        event.preventDefault();
        model.undo();
        return;
      }
      if (meta && key === 'Home') {
        event.preventDefault();
        applySelection({ r1: 1, c1: 1, r2: 1, c2: 1 }, { row: 1, col: 1 }, { row: 1, col: 1 }, true);
        return;
      }
      switch (key) {
        case 'ArrowUp': event.preventDefault(); moveActive(-1, 0, event.shiftKey); return;
        case 'ArrowDown': event.preventDefault(); moveActive(1, 0, event.shiftKey); return;
        case 'ArrowLeft': event.preventDefault(); moveActive(0, -1, event.shiftKey); return;
        case 'ArrowRight': event.preventDefault(); moveActive(0, 1, event.shiftKey); return;
        case 'Tab': event.preventDefault(); moveActive(0, event.shiftKey ? -1 : 1, false); return;
        case 'Enter': event.preventDefault(); moveActive(event.shiftKey ? -1 : 1, 0, false); return;
        case 'F2': event.preventDefault(); startEditing(selection.active.row, selection.active.col, null); return;
        case 'Escape':
          applySelection(
            { r1: selection.active.row, c1: selection.active.col, r2: selection.active.row, c2: selection.active.col },
            selection.active,
            selection.active,
            false,
          );
          return;
        case 'Home': {
          event.preventDefault();
          const col = frozenCols + 1;
          const target = { row: selection.active.row, col: col };
          applySelection({ r1: target.row, c1: col, r2: target.row, c2: col }, target, target, true);
          return;
        }
        case 'End': {
          event.preventDefault();
          const col = sheet ? sheet.maxCol : 1;
          const target = { row: selection.active.row, col: col };
          applySelection({ r1: target.row, c1: col, r2: target.row, c2: col }, target, target, true);
          return;
        }
        case 'Delete':
        case 'Backspace':
          event.preventDefault();
          clearRange();
          return;
        default:
          break;
      }
      // Typing over a cell replaces it (plan 8.1 "typing to replace").
      if (!meta && !event.altKey && key.length === 1) {
        event.preventDefault();
        startEditing(selection.active.row, selection.active.col, key);
      }
    },
    [editing, model, applySelection, moveActive, startEditing, selection, sheet, frozenCols, clearRange],
  );

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLDivElement>) => {
      if (readOnly || editing || !sheet) return;
      const data = event.clipboardData ? event.clipboardData.getData('text/plain') : '';
      if (!data) return;
      event.preventDefault();
      // Split on /\r?\n/ — the working copy mixes line endings.
      const lines = data.replace(/\r?\n$/, '').split(/\r?\n/);
      const block = lines.map((line) => line.split('\t'));
      model.pasteBlock(sheetName, selection.active.row, selection.active.col, block);
    },
    [readOnly, editing, sheet, model, sheetName, selection],
  );

  const handleCopy = useCallback(
    (event: React.ClipboardEvent<HTMLDivElement>) => {
      if (!sheet || !event.clipboardData) return;
      const lines: string[] = [];
      for (let r = selection.range.r1; r <= selection.range.r2; r += 1) {
        const parts: string[] = [];
        for (let c = selection.range.c1; c <= selection.range.c2; c += 1) {
          const ref2 = formatCellRef(r, c);
          const described = describeCell(
            model.ctx, sheetName, ref2, cellAt(sheet, r, c), sheet.edited.has(keyOf(r, c)),
            changeIndex.get(changeKey(sheetName, ref2)) || null,
          );
          parts.push(described.value === null ? '' : described.value);
        }
        lines.push(parts.join('\t'));
      }
      event.clipboardData.setData('text/plain', lines.join('\n'));
      event.preventDefault();
    },
    [sheet, selection, model.ctx, sheetName, changeIndex],
  );

  const selectSheet = useCallback(
    (name: string) => {
      if (activeSheet === undefined) setUncontrolledSheet(name);
      if (onActiveSheetChange) onActiveSheetChange(name);
      setEditing(null);
      setMenu(null);
    },
    [activeSheet, onActiveSheetChange],
  );

  useImperativeHandle(
    ref,
    (): BudgetGridHandle => ({
      insertColumn: (request) => {
        model.insertColumn(
          request.sheet || sheetName,
          request.atCol,
          typeof request.count === 'number' ? request.count : 1,
          request.header === undefined ? null : request.header,
        );
      },
      insertRow: (request) => {
        model.insertRow(request.sheet || sheetName, request.atRow, typeof request.count === 'number' ? request.count : 1);
      },
      undo: () => model.undo(),
      canUndo: () => model.canUndo,
      getSheets: () => model.getSheets(),
      focusCell: (request) => {
        if (request.sheet && request.sheet !== sheetName) selectSheet(request.sheet);
        applySelection(
          { r1: request.row, c1: request.col, r2: request.row, c2: request.col },
          { row: request.row, col: request.col },
          { row: request.row, col: request.col },
          true,
        );
      },
      setActiveSheet: selectSheet,
    }),
    [model, sheetName, applySelection, selectSheet],
  );

  const handleScroll = useCallback((event: GridOnScrollProps) => {
    if (headerRef.current) headerRef.current.scrollTo({ scrollLeft: event.scrollLeft, scrollTop: 0 });
    if (leftRef.current) leftRef.current.scrollTo({ scrollLeft: 0, scrollTop: event.scrollTop });
  }, []);

  const handleItemsRendered = useCallback((event: GridOnItemsRenderedProps) => {
    visibleRef.current = {
      rowStart: event.visibleRowStartIndex,
      rowStop: event.visibleRowStopIndex,
      colStart: event.visibleColumnStartIndex,
      colStop: event.visibleColumnStopIndex,
    };
  }, []);

  const renderCell = useCallback(
    (row: number, col: number, style: React.CSSProperties, band: Band) => {
      if (!sheet) return null;
      const ref2 = formatCellRef(row, col);
      const cell = cellAt(sheet, row, col);
      const edited = sheet.edited.has(keyOf(row, col));
      const change = changeIndex.get(changeKey(sheetName, ref2)) || null;
      const described = describeCell(model.ctx, sheetName, ref2, cell, edited, change);
      const isActive = selection.active.row === row && selection.active.col === col;
      const inRange =
        row >= selection.range.r1 && row <= selection.range.r2
        && col >= selection.range.c1 && col <= selection.range.c2;
      const isEditing = editing !== null && editing.row === row && editing.col === col && editing.sheet === sheetName;
      const isHeaderBand = band === 'header' || band === 'corner' || band === 'label';

      let fillKind: string | undefined;
      let background = '';
      if (change && change.status === 'proposed') {
        fillKind = 'proposed';
        background = 'bg-amber-50 dark:bg-amber-900/20';
      } else if ((change && change.status === 'accepted') || edited) {
        fillKind = change ? 'accepted' : 'changed';
        background = 'bg-primary-50 dark:bg-primary-900/20';
      } else if (inRange && !isActive) {
        background = 'bg-primary-50/60 dark:bg-primary-900/10';
      } else if (isHeaderBand) {
        background = 'bg-gray-50 dark:bg-gray-800/50';
      }

      const tone = change && change.status === 'proposed'
        ? 'text-amber-800 dark:text-amber-300'
        : (isHeaderBand ? 'text-gray-700 dark:text-gray-200' : 'text-gray-900 dark:text-gray-100');

      const classes = [
        cellBase,
        background,
        tone,
        isHeaderBand ? 'text-xs font-medium' : 'text-sm',
        described.align === 'right' ? 'justify-end tabular-nums' : 'justify-start',
        isActive ? 'z-10 ring-2 ring-inset ring-primary-500' : '',
        readOnly ? 'cursor-default' : 'cursor-cell',
      ].filter(Boolean).join(' ');

      return (
        <div
          key={ref2}
          style={style}
          className={classes}
          title={described.title}
          data-testid={'gc-' + ref2}
          data-band={band}
          data-label={described.label}
          data-state={described.state}
          data-fill={fillKind}
          data-active={isActive ? 'true' : undefined}
          onMouseDown={(event) => {
            if (event.button !== 0) return;
            setMenu(null);
            if (event.shiftKey) {
              applySelection(
                normalise(selection.active, { row: row, col: col }),
                selection.active,
                { row: row, col: col },
                false,
              );
            } else {
              applySelection({ r1: row, c1: col, r2: row, c2: col }, { row: row, col: col }, { row: row, col: col }, true);
            }
            if (containerRef.current) containerRef.current.focus();
          }}
          onDoubleClick={() => startEditing(row, col, null)}
          onContextMenu={(event) => {
            event.preventDefault();
            applySelection({ r1: row, c1: col, r2: row, c2: col }, { row: row, col: col }, { row: row, col: col }, true);
            setMenu({ x: event.clientX, y: event.clientY, row: row, col: col });
          }}
          onMouseEnter={() => {
            if (!onCommentHover) return;
            const event = focusEventFor(row, col);
            if (event && event.comment) onCommentHover(event);
          }}
          onMouseLeave={() => {
            if (onCommentHover) onCommentHover(null);
          }}
        >
          {isEditing && editing ? (
            <input
              data-testid="gc-editor"
              aria-label={'Edit ' + sheetName + '!' + ref2}
              className="absolute inset-0 h-full w-full border-0 bg-white px-2 text-sm text-gray-900 outline-none ring-2 ring-inset ring-primary-500 dark:bg-gray-900 dark:text-gray-100"
              value={editing.text}
              autoFocus
              onChange={(event) => setEditing({ sheet: sheetName, row: row, col: col, text: event.target.value })}
              onBlur={() => {
                if (skipBlurRef.current) { skipBlurRef.current = false; return; }
                commitEditing(null);
              }}
              onKeyDown={(event) => {
                event.stopPropagation();
                if (event.key === 'Enter') {
                  event.preventDefault();
                  skipBlurRef.current = true;
                  commitEditing({ row: row + (event.shiftKey ? -1 : 1), col: col });
                } else if (event.key === 'Tab') {
                  event.preventDefault();
                  skipBlurRef.current = true;
                  commitEditing({ row: row, col: col + (event.shiftKey ? -1 : 1) });
                } else if (event.key === 'Escape') {
                  event.preventDefault();
                  skipBlurRef.current = true;
                  setEditing(null);
                  if (containerRef.current) containerRef.current.focus();
                }
              }}
            />
          ) : (
            <>
              <span className="truncate" data-testid={'gc-value-' + ref2}>{described.text}</span>
              {cell && cell.comment_text ? (
                <span
                  aria-hidden="true"
                  data-testid={'gc-comment-' + ref2}
                  className="pointer-events-none absolute left-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-amber-500"
                />
              ) : null}
              {showLabels && described.state !== 'empty' && described.isNumeric ? (
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute right-0.5 top-0 text-[9px] leading-none text-gray-400 dark:text-gray-500"
                >
                  {described.label.charAt(0).toUpperCase()}
                </span>
              ) : null}
            </>
          )}
        </div>
      );
    },
    [
      sheet, sheetName, changeIndex, model.ctx, selection, editing, readOnly, showLabels,
      applySelection, startEditing, commitEditing, focusEventFor, onCommentHover,
    ],
  );

  if (model.error) {
    return (
      <div
        data-testid="budget-grid-error"
        className="flex flex-col items-center justify-center rounded-xl border border-dashed border-red-300 p-10 text-center dark:border-red-800"
      >
        <p className="text-lg font-medium text-gray-900 dark:text-gray-100">This workbook cannot be opened</p>
        <p className="mt-1 max-w-sm text-sm text-gray-500 dark:text-gray-400">{model.error}</p>
      </div>
    );
  }

  if (!sheet) {
    return (
      <div
        data-testid="budget-grid-empty"
        className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 p-10 text-center dark:border-gray-600"
      >
        <p className="text-lg font-medium text-gray-900 dark:text-gray-100">No sheets in this workspace</p>
        <p className="mt-1 max-w-sm text-sm text-gray-500 dark:text-gray-400">
          The workspace payload carried no sheet the grid could open.
        </p>
      </div>
    );
  }

  const cornerCells: React.ReactNode[] = [];
  {
    let top = 0;
    for (let r = 0; r <= frozenRows; r += 1) {
      let left = 0;
      const h = r === 0 ? RULER_HEIGHT : rowHeight;
      for (let c = 0; c <= frozenCols; c += 1) {
        const w = c === 0 ? RULER_WIDTH : widthOf(c);
        const style: React.CSSProperties = { position: 'absolute', left: left, top: top, width: w, height: h };
        if (r === 0 || c === 0) {
          const label = r === 0 && c === 0 ? '' : (r === 0 ? columnIndexToLetter(c) : String(r));
          cornerCells.push(
            <div
              key={'corner-' + String(r) + '-' + String(c)}
              style={style}
              data-testid={r === 0 && c > 0 ? 'gc-colhead-' + columnIndexToLetter(c) : (c === 0 && r > 0 ? 'gc-rowhead-' + String(r) : undefined)}
              className={cellBase + rulerCellClass}
            >
              {label}
            </div>,
          );
        } else {
          cornerCells.push(renderCell(r, c, style, 'corner'));
        }
        left += w;
      }
      top += h;
    }
  }

  // A fresh object on every render on purpose: react-window's grids are pure,
  // and this is what tells them the cells' closure moved on.
  const bandData: BandData = { renderCell: renderCell, frozenRows: frozenRows, frozenCols: frozenCols };

  const gridClass =
    'overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 '
    + (className || '');

  return (
    <div className={gridClass} data-testid="budget-grid" data-readonly={readOnly ? 'true' : undefined}>
      {visibleSheets.length > 1 ? (
        <div className="flex items-center gap-1 border-b border-gray-200 px-2 dark:border-gray-700" role="tablist">
          {visibleSheets.map((s) => {
            const active = s.name === sheetName;
            return (
              <button
                key={s.name}
                type="button"
                role="tab"
                aria-selected={active}
                data-testid={'gc-sheet-' + s.name}
                onClick={() => selectSheet(s.name)}
                className={
                  'min-h-[44px] border-b-2 px-2.5 text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0 sm:py-2 '
                  + (active
                    ? 'border-primary-600 text-primary-700 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200')
                }
              >
                {s.name}
              </button>
            );
          })}
        </div>
      ) : null}

      <div
        ref={containerRef}
        tabIndex={0}
        role="grid"
        aria-label={'Budget grid, sheet ' + sheetName}
        aria-readonly={readOnly ? true : undefined}
        data-testid="gc-surface"
        className="relative outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
        style={{ width: width, height: height }}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onCopy={handleCopy}
      >
        <div className="absolute left-0 top-0" style={{ width: cornerWidth, height: headerHeight }}>
          {cornerCells}
        </div>

        <div className="absolute top-0" style={{ left: cornerWidth, width: bodyWidth, height: headerHeight }}>
          <VariableSizeGrid
            ref={headerRef}
            columnCount={bodyColCount}
            rowCount={frozenRows + 1}
            columnWidth={(index) => widthOf(frozenCols + 1 + index)}
            rowHeight={(index) => (index === 0 ? RULER_HEIGHT : rowHeight)}
            width={bodyWidth}
            height={headerHeight}
            style={{ overflow: 'hidden' }}
            itemData={bandData}
          >
            {HeaderBandCell}
          </VariableSizeGrid>
        </div>

        <div className="absolute left-0" style={{ top: headerHeight, width: cornerWidth, height: bodyHeight }}>
          <VariableSizeGrid
            ref={leftRef}
            columnCount={frozenCols + 1}
            rowCount={bodyRowCount}
            columnWidth={(index) => (index === 0 ? RULER_WIDTH : widthOf(index))}
            rowHeight={() => rowHeight}
            width={cornerWidth}
            height={bodyHeight}
            style={{ overflow: 'hidden' }}
            itemData={bandData}
          >
            {LeftBandCell}
          </VariableSizeGrid>
        </div>

        <div className="absolute" style={{ left: cornerWidth, top: headerHeight, width: bodyWidth, height: bodyHeight }}>
          <VariableSizeGrid
            ref={bodyRef}
            columnCount={bodyColCount}
            rowCount={bodyRowCount}
            columnWidth={(index) => widthOf(frozenCols + 1 + index)}
            rowHeight={() => rowHeight}
            width={bodyWidth}
            height={bodyHeight}
            onScroll={handleScroll}
            onItemsRendered={handleItemsRendered}
            itemData={bandData}
          >
            {BodyCell}
          </VariableSizeGrid>
        </div>

        {menu && !readOnly ? (
          <div
            data-testid="gc-menu"
            className="absolute z-20 w-56 rounded-xl border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800"
            style={{ left: Math.min(menu.x, Math.max(0, width - 230)), top: Math.min(menu.y, Math.max(0, height - 160)) }}
          >
            {[
              { id: 'insert-col-left', label: 'Insert column left', run: () => model.insertColumn(sheetName, menu.col, 1, null) },
              { id: 'insert-col-right', label: 'Insert column right', run: () => model.insertColumn(sheetName, menu.col + 1, 1, null) },
              { id: 'insert-row-above', label: 'Insert row above', run: () => model.insertRow(sheetName, menu.row, 1) },
              { id: 'insert-row-below', label: 'Insert row below', run: () => model.insertRow(sheetName, menu.row + 1, 1) },
            ].map((item) => (
              <button
                key={item.id}
                type="button"
                data-testid={'gc-menu-' + item.id}
                className="flex min-h-[44px] w-full items-center px-3 text-left text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0 sm:py-1.5"
                onClick={() => { item.run(); setMenu(null); }}
              >
                {item.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <div
        data-testid="gc-status"
        className="flex items-center gap-3 border-t border-gray-200 px-3 py-1.5 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400"
      >
        <span className="font-medium text-gray-700 dark:text-gray-200">
          {sheetName}!{formatCellRef(selection.active.row, selection.active.col)}
        </span>
        <span className="truncate">
          {(() => {
            const described = describeCell(
              model.ctx, sheetName, formatCellRef(selection.active.row, selection.active.col),
              cellAt(sheet, selection.active.row, selection.active.col),
              sheet.edited.has(keyOf(selection.active.row, selection.active.col)),
              changeIndex.get(changeKey(sheetName, formatCellRef(selection.active.row, selection.active.col))) || null,
            );
            return described.formula || described.text;
          })()}
        </span>
        {readOnly ? (
          <span className="ml-auto rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
            Read only
          </span>
        ) : null}
      </div>
    </div>
  );
});
